import type { TeamWith } from "@redotech/account-common/base-team";
import { RedoMerchantRpcClient } from "@redotech/merchant-sdk/rpc/client";
import { TagKind } from "@redotech/redo-common-model-tags-common/tag";
import { FulfillmentGroupFilterName } from "@redotech/redo-fulfillment-common/fulfillment-group-summary-input";
import {
  FulfillmentGroupStatus,
  HiddenStatuses,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group-status";
import { OMSUser } from "@redotech/redo-fulfillment-group-common/oms-user";
import { toTitleCase } from "@redotech/redo-fulfillment-operations-common/views/default-outbound-views";
import {
  FGFilterKey,
  FGWmsFilterKey,
  fgFilterEntries,
} from "@redotech/redo-fulfillment-operations-common/views/fulfillment/fg-filter-catalog";
import { WeightFormat } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import type { OutboundLabelSettings } from "@redotech/redo-fulfillment-settings/outbound-label-settings";
import { RedoOutboundLabelsRpcClient } from "@redotech/redo-merchant-fulfillment-rpc/client";
import { Provider } from "@redotech/redo-order-common/provider";
import { AdvancedTableFilter } from "@redotech/redo-web/advanced-filters/advanced-filter";
import { FulfilledFilter } from "../../dashboard/filters/fulfilled-filter";
import { AddressVerifiedFilter } from "./address-verified-filter";
import { AssignedFulfillmentServiceFilter } from "./assigned-fulfillment-service-filter";
import { AssignedPickerFilter } from "./assigned-picker-filter";
import { BatchIdFilter } from "./batch-id-filter";
import { BinFilter } from "./bin-filter";
import { CommercialInvoiceElectronicFilter } from "./commercial-invoice-electronic-filter";
import { CountriesFilter } from "./countries-filter";
import { CreationDateFilter } from "./create-date-filter";
import { getCustomFieldDateFilter } from "./custom-field-date-filter";
import { getCustomFieldNumberFilter } from "./custom-field-number-filter";
import { getCustomFieldStringFilter } from "./custom-field-string-filter";
import { CustomerNameFilter } from "./customer-name-filter";
import { CustomerOrderCountFilter } from "./customer-order-count-filter";
import { DeliveryMethodFilter } from "./delivery-method-filter";
import { DeliveryStatusFilter } from "./delivery-status-filter";
import { EstimatedDeliveryDateFilter } from "./estimated-delivery-date-filter";
import { ExpandedItemQuantityFilter } from "./expanded-item-quantity-filter";
import { FraudRiskFilter } from "./fraud-risk-filter";
import { FulfilledByArrayFilter } from "./fulfilled-by-filter";
import { FulfillmentOrderCountFilter } from "./fulfillment-order-count-filter";
import { ItemQuantityFilter } from "./item-quantity-filter";
import { ItemSkusFilter } from "./item-skus-filter";
import { ItemTitleFilter } from "./item-title-filter";
import { LabelPurchasedDateFilter } from "./label-purchased-date-filter";
import { MarketplaceNotificationStatusArrayFilter } from "./marketplace-notification-status-filter";
import { MissingBinsFilter } from "./missing-bins-filter";
import { MissingPickItemSkusFilter } from "./missing-pick-item-skus-filter";
import { NoNonExpiredInventoryInWarehouseFilter } from "./no-non-expired-inventory-filter";
import { NumberOfLineItemsFilter } from "./number-of-line-items-filter";
import { OMSUserFilter } from "./oms-user-filter";
import { OrderCreatedDateFilter } from "./order-created-at-filter";
import { OrderFilter } from "./order-filter";
import { OrderNoteFilter } from "./order-note-filter";
import { OriginAddressNameFilter } from "./origin-address-name-filter";
import { OriginStoreFilter } from "./origin-store-filter";
import { PackingSlipPrintedAtFilter } from "./packing-slip-printed-at-filter";
import { PickSessionExistsFilter } from "./pick-session-exists-filter";
import { PickSessionIdFilter } from "./pick-session-id-filter";
import { PickStartedOnFilter } from "./pick-started-on-filter";
import { PickStatusArrayFilter } from "./pick-status-filter";
import { PickabilityFilter } from "./pickability-filter";
import { PickableBinCountFilter } from "./pickable-bin-count-filter";
import { PickedAtFilter } from "./picked-at-filter";
import { PrinterFilter } from "./printer-filter";
import { ProductBinAssignmentDateFilter } from "./product-bin-assignment-date-filter";
import { ProductTagsFilter } from "./product-tags-filter";
import { ProductVariantFilter } from "./product-variant-filter";
import { RequestedServiceFilter } from "./requested-service-filter";
import { SalesChannelFilter } from "./sales-channel-filter";
import { ScanVerifiedFilter } from "./scan-verified-filter";
import { SelectedCarrierFilter } from "./selected-carrier-filter";
import { SelectedParcelsNameFilter } from "./selected-parcels-name-filter";
import { ShippedDateFilter } from "./shipped-date-filter";
import { ShopifyTagFilter } from "./shopify-tag-filter";
import { SLADeliveryPromiseFilter } from "./sla-filter";
import { OrderSourceFilter } from "./source-filter";
import { StateFilter } from "./state-filter";
import { StatusArrayFilter } from "./status-filter";
import { TagFilter } from "./tag-filter";
import { TotalPriceFilter } from "./total-price-filter";
import { UsedParcelLearningFilter } from "./used-parcel-learning-filter";
import { UsedWeightLearningFilter } from "./used-weight-learning-filter";
import { VendorsFilter } from "./vendors-filter";
import { VoidedBatchIdFilter } from "./voided-batch-id-filter";
import { WarehouseMissingBinsFilter } from "./warehouse-missing-bins-filter";
import { WeightFilter } from "./weight-filter";

export function getFGAdvancedFilters({
  team,
  users,
  rpcClient,
  rpcMerchantClient,
  enabledProviders,
}: {
  team: TeamWith<{ outboundLabels: OutboundLabelSettings }>;
  users: OMSUser[];
  rpcClient: RedoOutboundLabelsRpcClient;
  rpcMerchantClient: RedoMerchantRpcClient;
  enabledProviders: readonly Provider[];
}): AdvancedTableFilter[] {
  const wmsEnabled = team?.settings?.outboundLabels?.wms?.enabled ?? false;
  const wmsSotEnabled =
    team?.settings?.outboundLabels?.wms?.sourceOfTruthEnabled ?? false;

  // Use a record here to ensure that anytime we try to add a filter in the UI, we have to add it to the filter catalog entries in the fgFilterEntries object.
  const baseFilterMap: Record<FGFilterKey, AdvancedTableFilter> = {
    createdAt: CreationDateFilter,
    source: OrderSourceFilter,
    status: StatusArrayFilter(
      createFulfillmentGroupStatusFetcher(enabledProviders),
    ),
    orderCreatedAt: OrderCreatedDateFilter,
    fulfilledDate: FulfilledFilter,
    itemQuantity: ItemQuantityFilter,
    expandedItemQuantity: ExpandedItemQuantityFilter,
    totalPrice: TotalPriceFilter,
    parcelWeight: WeightFilter(
      team?.settings?.outboundLabels?.weightFormat ?? WeightFormat.IMPERIAL,
    ),
    lineItemQuantity: NumberOfLineItemsFilter,
    deliveryMethod: DeliveryMethodFilter,
    selectedCarrier: SelectedCarrierFilter,
    orderNote: OrderNoteFilter,
    shippedDate: ShippedDateFilter,
    labelPurchasedDate: LabelPurchasedDateFilter,
    slaDeliveryPromise: SLADeliveryPromiseFilter,
    itemSkus: ItemSkusFilter(createValueFetcher("itemSkus", rpcClient)),
    countries: CountriesFilter(createValueFetcher("countries", rpcClient)),
    requestedService: RequestedServiceFilter(
      createValueFetcher("requestedService", rpcClient),
    ),
    assignees: OMSUserFilter(users),
    itemTitle: ItemTitleFilter(createValueFetcher("itemTitle", rpcClient)),
    stores: SalesChannelFilter(createValueFetcher("stores", rpcClient)),
    originStore: OriginStoreFilter(
      createValueFetcher("originStore", rpcClient),
    ),
    fraudRisk: FraudRiskFilter(),
    batchId: BatchIdFilter(createValueFetcher("batchId", rpcClient)),
    voidedBatchId: VoidedBatchIdFilter(
      createValueFetcher("voidedBatchId", rpcClient),
    ),
    productVariant: ProductVariantFilter(
      createValueFetcher("productVariant", rpcClient),
    ),
    vendors: VendorsFilter(createValueFetcher("vendors", rpcClient)),
    fulfillmentLocation: OriginAddressNameFilter(
      createValueFetcher("originAddressName", rpcClient),
    ),
    customerOrderCount: CustomerOrderCountFilter,
    customer: CustomerNameFilter(createValueFetcher("customer", rpcClient)),
    tagName: TagFilter(
      async () =>
        (
          await rpcMerchantClient.getTeamTags({
            kind: TagKind.FULFILLMENT_GROUP,
          })
        ).tags,
    ),
    shopifyOrderTags: ShopifyTagFilter(
      createValueFetcher("shopifyOrderTags", rpcClient),
    ),
    productTags: ProductTagsFilter(
      createValueFetcher("productTags", rpcClient),
    ),
    order: OrderFilter(createValueFetcher("order", rpcClient)),
    labelPrinted: PrinterFilter(fgFilterEntries.labelPrinted, "Label"),
    pickListPrinted: PrinterFilter(
      fgFilterEntries.pickListPrinted,
      "Pick list",
    ),
    packingSlipPrinted: PrinterFilter(
      fgFilterEntries.packingSlipPrinted,
      "Packing slip",
    ),
    scanVerified: ScanVerifiedFilter,
    fulfilledBy: FulfilledByArrayFilter,
    commercialInvoiceElectronic: CommercialInvoiceElectronicFilter,
    addressVerified: AddressVerifiedFilter,
    bin: BinFilter(createValueFetcher("bin", rpcClient)),
    deliveryStatus: DeliveryStatusFilter,
    estimatedDeliveryDate: EstimatedDeliveryDateFilter,
    state: StateFilter,
    packingSlipPrintedAt: PackingSlipPrintedAtFilter,
    fulfillmentOrderCount: FulfillmentOrderCountFilter,
    missingBins: MissingBinsFilter,
    usedWeightLearning: UsedWeightLearningFilter,
    usedParcelLearning: UsedParcelLearningFilter,
    selectedParcelsName: SelectedParcelsNameFilter(
      createValueFetcher("selectedParcelsName", rpcClient),
    ),
    pickSessionId: PickSessionExistsFilter,
    pickSessionIdValue: PickSessionIdFilter,
    assignedFulfillmentService: AssignedFulfillmentServiceFilter(rpcClient),
    marketplaceNotificationStatus: MarketplaceNotificationStatusArrayFilter,
  };

  // Use a record here to ensure that anytime we try to add a filter in the UI, we have to add it to the filter catalog entries in the fgWmsFilterEntries object.
  const wmsFilterMap: Record<FGWmsFilterKey, AdvancedTableFilter> = {
    pickedAt: PickedAtFilter,
    pickStartedOn: PickStartedOnFilter,
    pickStatus: PickStatusArrayFilter(),
    assignedPicker: AssignedPickerFilter(users),
    pickability: PickabilityFilter(),
    missingPickItemSkus: MissingPickItemSkusFilter(
      createValueFetcher("missingPickItemSkus", rpcClient),
    ),
    warehouseMissingBins: WarehouseMissingBinsFilter,
    pickableBinCount: PickableBinCountFilter,
    productBinAssignmentDate: ProductBinAssignmentDateFilter,
  };

  const advancedFilters: AdvancedTableFilter[] = Object.values(baseFilterMap);

  if (wmsEnabled) {
    advancedFilters.push(...Object.values(wmsFilterMap));
  }

  if (wmsSotEnabled) {
    advancedFilters.push(NoNonExpiredInventoryInWarehouseFilter);
  }

  const customAttributes = team?.settings?.outboundLabels?.customAttributes;

  if (customAttributes?.customAttributeDate1) {
    advancedFilters.push(
      getCustomFieldDateFilter(
        "customAttributeDate1",
        customAttributes?.customAttributeDate1?.displayName ||
          customAttributes?.customAttributeDate1?.key,
      ),
    );
  }

  if (customAttributes?.customAttributeDate2) {
    advancedFilters.push(
      getCustomFieldDateFilter(
        "customAttributeDate2",
        customAttributes?.customAttributeDate2?.displayName ||
          customAttributes?.customAttributeDate2?.key,
      ),
    );
  }

  if (customAttributes?.customAttributeDate3) {
    advancedFilters.push(
      getCustomFieldDateFilter(
        "customAttributeDate3",
        customAttributes?.customAttributeDate3?.displayName ||
          customAttributes?.customAttributeDate3?.key,
      ),
    );
  }

  if (customAttributes?.customAttributeNumber1) {
    advancedFilters.push(
      getCustomFieldNumberFilter(
        "customAttributeNumber1",
        customAttributes?.customAttributeNumber1?.displayName ||
          customAttributes?.customAttributeNumber1?.key,
      ),
    );
  }

  if (customAttributes?.customAttributeNumber2) {
    advancedFilters.push(
      getCustomFieldNumberFilter(
        "customAttributeNumber2",
        customAttributes?.customAttributeNumber2?.displayName ||
          customAttributes?.customAttributeNumber2?.key,
      ),
    );
  }

  if (customAttributes?.customAttributeNumber3) {
    advancedFilters.push(
      getCustomFieldNumberFilter(
        "customAttributeNumber3",
        customAttributes?.customAttributeNumber3?.displayName ||
          customAttributes?.customAttributeNumber3?.key,
      ),
    );
  }

  if (customAttributes?.customAttributeString1) {
    advancedFilters.push(
      getCustomFieldStringFilter(
        "customAttributeString1",
        customAttributes?.customAttributeString1?.displayName ||
          customAttributes?.customAttributeString1?.key,
        createValueFetcher("customAttributeString1", rpcClient),
      ),
    );
  }

  if (customAttributes?.customAttributeString2) {
    advancedFilters.push(
      getCustomFieldStringFilter(
        "customAttributeString2",
        customAttributes?.customAttributeString2?.displayName ||
          customAttributes?.customAttributeString2?.key,
        createValueFetcher("customAttributeString2", rpcClient),
      ),
    );
  }

  if (customAttributes?.customAttributeString3) {
    advancedFilters.push(
      getCustomFieldStringFilter(
        "customAttributeString3",
        customAttributes?.customAttributeString3?.displayName ||
          customAttributes?.customAttributeString3?.key,
        createValueFetcher("customAttributeString3", rpcClient),
      ),
    );
  }

  const customMetadata = team?.settings?.outboundLabels?.customMetadata;

  if (customMetadata?.customDate1) {
    advancedFilters.push(
      getCustomFieldDateFilter(
        "customDate1",
        customMetadata?.customDate1?.displayName ||
          customMetadata?.customDate1?.key,
      ),
    );
  }

  if (customMetadata?.customDate2) {
    advancedFilters.push(
      getCustomFieldDateFilter(
        "customDate2",
        customMetadata?.customDate2?.displayName ||
          customMetadata?.customDate2?.key,
      ),
    );
  }

  if (customMetadata?.customDate3) {
    advancedFilters.push(
      getCustomFieldDateFilter(
        "customDate3",
        customMetadata?.customDate3?.displayName ||
          customMetadata?.customDate3?.key,
      ),
    );
  }

  if (customMetadata?.customNumber1) {
    advancedFilters.push(
      getCustomFieldNumberFilter(
        "customNumber1",
        customMetadata?.customNumber1?.displayName ||
          customMetadata?.customNumber1?.key,
      ),
    );
  }

  if (customMetadata?.customNumber2) {
    advancedFilters.push(
      getCustomFieldNumberFilter(
        "customNumber2",
        customMetadata?.customNumber2?.displayName ||
          customMetadata?.customNumber2?.key,
      ),
    );
  }

  if (customMetadata?.customNumber3) {
    advancedFilters.push(
      getCustomFieldNumberFilter(
        "customNumber3",
        customMetadata?.customNumber3?.displayName ||
          customMetadata?.customNumber3?.key,
      ),
    );
  }

  if (customMetadata?.customString1) {
    advancedFilters.push(
      getCustomFieldStringFilter(
        "customString1",
        customMetadata?.customString1?.displayName ||
          customMetadata?.customString1?.key,
        createValueFetcher("customString1", rpcClient),
      ),
    );
  }

  if (customMetadata?.customString2) {
    advancedFilters.push(
      getCustomFieldStringFilter(
        "customString2",
        customMetadata?.customString2?.displayName ||
          customMetadata?.customString2?.key,
        createValueFetcher("customString2", rpcClient),
      ),
    );
  }

  if (customMetadata?.customString3) {
    advancedFilters.push(
      getCustomFieldStringFilter(
        "customString3",
        customMetadata?.customString3?.displayName ||
          customMetadata?.customString3?.key,
        createValueFetcher("customString3", rpcClient),
      ),
    );
  }

  const variantCustomMetadata =
    team?.settings?.outboundLabels?.variantCustomMetadata;

  if (variantCustomMetadata?.variantCustomDate1) {
    advancedFilters.push(
      getCustomFieldDateFilter(
        "variantCustomDate1",
        variantCustomMetadata?.variantCustomDate1?.displayName ||
          variantCustomMetadata?.variantCustomDate1?.key,
      ),
    );
  }

  if (variantCustomMetadata?.variantCustomDate2) {
    advancedFilters.push(
      getCustomFieldDateFilter(
        "variantCustomDate2",
        variantCustomMetadata?.variantCustomDate2?.displayName ||
          variantCustomMetadata?.variantCustomDate2?.key,
      ),
    );
  }

  if (variantCustomMetadata?.variantCustomDate3) {
    advancedFilters.push(
      getCustomFieldDateFilter(
        "variantCustomDate3",
        variantCustomMetadata?.variantCustomDate3?.displayName ||
          variantCustomMetadata?.variantCustomDate3?.key,
      ),
    );
  }

  if (variantCustomMetadata?.variantCustomNumber1) {
    advancedFilters.push(
      getCustomFieldNumberFilter(
        "variantCustomNumber1",
        variantCustomMetadata?.variantCustomNumber1?.displayName ||
          variantCustomMetadata?.variantCustomNumber1?.key,
      ),
    );
  }

  if (variantCustomMetadata?.variantCustomNumber2) {
    advancedFilters.push(
      getCustomFieldNumberFilter(
        "variantCustomNumber2",
        variantCustomMetadata?.variantCustomNumber2?.displayName ||
          variantCustomMetadata?.variantCustomNumber2?.key,
      ),
    );
  }

  if (variantCustomMetadata?.variantCustomNumber3) {
    advancedFilters.push(
      getCustomFieldNumberFilter(
        "variantCustomNumber3",
        variantCustomMetadata?.variantCustomNumber3?.displayName ||
          variantCustomMetadata?.variantCustomNumber3?.key,
      ),
    );
  }

  if (variantCustomMetadata?.variantCustomString1) {
    advancedFilters.push(
      getCustomFieldStringFilter(
        "variantCustomString1",
        variantCustomMetadata?.variantCustomString1?.displayName ||
          variantCustomMetadata?.variantCustomString1?.key,
        createValueFetcher("variantCustomString1", rpcClient),
      ),
    );
  }

  if (variantCustomMetadata?.variantCustomString2) {
    advancedFilters.push(
      getCustomFieldStringFilter(
        "variantCustomString2",
        variantCustomMetadata?.variantCustomString2?.displayName ||
          variantCustomMetadata?.variantCustomString2?.key,
        createValueFetcher("variantCustomString2", rpcClient),
      ),
    );
  }

  if (variantCustomMetadata?.variantCustomString3) {
    advancedFilters.push(
      getCustomFieldStringFilter(
        "variantCustomString3",
        variantCustomMetadata?.variantCustomString3?.displayName ||
          variantCustomMetadata?.variantCustomString3?.key,
        createValueFetcher("variantCustomString3", rpcClient),
      ),
    );
  }

  return advancedFilters.sort((a, b) => {
    return a.displayName.localeCompare(b.displayName);
  });
}

export function createValueFetcher(
  filterName: string,
  rpcClient: RedoOutboundLabelsRpcClient,
): (
  {
    filters,
    filterSearch,
    applyTableState,
  }: {
    filters: AdvancedTableFilter[];
    filterSearch?: string;
    applyTableState?: boolean;
  },
  signal: AbortSignal,
) => Promise<string[]> {
  return async ({ filters, filterSearch, applyTableState }, signal) => {
    const { values } = await rpcClient.getFulfillmentGroupUniqueValues(
      {
        advancedFiltersData: applyTableState
          ? filters.map((filter) => filter.data)
          : [],
        filterName: filterName as FulfillmentGroupFilterName,
        filterSearch,
      },
      { signal },
    );
    return values;
  };
}

// Custom fetcher for the status values to hide the OpenNotReceived status if the merchant does not have a CS integration
function createFulfillmentGroupStatusFetcher(
  enabledProviders: readonly Provider[],
): (
  {
    filters,
    filterSearch,
    applyTableState,
  }: {
    filters: AdvancedTableFilter[];
    filterSearch?: string;
    applyTableState?: boolean;
  },
  signal: AbortSignal,
) => Promise<string[]> {
  return async ({ filters, filterSearch, applyTableState }, signal) => {
    const isCommentsoldEnabled = enabledProviders.includes(
      Provider.COMMENTSOLD,
    );
    return Object.values(FulfillmentGroupStatus)
      .filter((status) => {
        if (
          status === FulfillmentGroupStatus.OpenNotReceived &&
          !isCommentsoldEnabled
        ) {
          return false;
        }

        return !HiddenStatuses.includes(status);
      })
      .map((status) => toTitleCase(status));
  };
}
